import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  Buildings, ChartLineUp, Kanban, CurrencyInr, Users, Plus, Sparkle,
  ArrowsClockwise, Copy, CheckCircle, ClockClockwise, LockKey, Package,
} from "@phosphor-icons/react";

const STAGE_META = {
  lead: { label: "Lead", tone: "bg-zinc-100 text-zinc-700 border-zinc-200" },
  demo: { label: "Demo", tone: "bg-blue-50 text-blue-700 border-blue-200" },
  trial: { label: "Trial", tone: "bg-amber-50 text-amber-700 border-amber-200" },
  active: { label: "Active", tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  churned: { label: "Churned", tone: "bg-red-50 text-red-700 border-red-200" },
};
const PACK_TONE = {
  essentials: "bg-zinc-100 text-zinc-700 border-zinc-200",
  growth: "bg-zenith-teal/10 text-zenith-teal-700 border-zenith-teal/30",
  enterprise: "bg-zenith-magenta/10 text-zenith-magenta border-zenith-magenta/30",
};

function fmtINR(n) { return `₹ ${Number(n || 0).toLocaleString("en-IN")}`; }

function copyToClipboard(text) {
  try { navigator.clipboard.writeText(text); toast.success("Copied"); }
  catch { toast.error("Copy failed"); }
}


// ---------------------------------------------------------------------------
// Create tenant dialog
// ---------------------------------------------------------------------------
function NewTenantDialog({ packs, stages, onCreated }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [parentTenant, setParentTenant] = useState(null);
  const [form, setForm] = useState({
    name: "", slug: "", pack: "growth", pipeline_stage: "trial", mrr: "", seat_count: "",
    trial_expiry: "", sales_notes: "", admin_email: "", admin_name: "",
  });

  useEffect(() => {
    if (!open || !user?.tenant_id) return;
    api.get(`/tenants/${user.tenant_id}`).then(({ data }) => setParentTenant(data)).catch(() => setParentTenant(null));
  }, [open, user?.tenant_id]);

  const isGroup = parentTenant?.tenant_type === "group";
  const isAgency = parentTenant?.tenant_type === "agency" || parentTenant?.is_agency;
  const childLabel = isGroup ? "Member Company" : (isAgency ? "Client Company" : "Tenant");
  const btnLabel = isGroup ? "Add Member Company" : (isAgency ? "Add Client Company" : "New tenant");

  const submit = async () => {
    if (!form.name.trim() || !form.slug.trim()) { toast.error("Company name and slug are required"); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        mrr: parseFloat(form.mrr) || 0,
        seat_count: parseInt(form.seat_count) || 0,
        trial_expiry: form.trial_expiry || undefined,
        admin_email: form.admin_email || undefined,
        admin_name: form.admin_name || undefined,
      };
      const { data } = await api.post("/sales/tenants", payload);
      setResult(data);
      toast.success("Tenant created");
      onCreated();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const close = () => {
    setOpen(false); setResult(null);
    setForm({ name: "", slug: "", pack: "growth", pipeline_stage: "trial", mrr: "", seat_count: "", trial_expiry: "", sales_notes: "", admin_email: "", admin_name: "" });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : close())}>
      <DialogTrigger asChild>
        <Button data-testid="sales-new-tenant-btn" className="rounded-sm bg-adb-navy hover:bg-adb-navy-800">
          <Plus size={14} className="mr-1" /> {btnLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Buildings size={16} weight="duotone" className="text-zenith-teal" />
            {result ? `${childLabel} created` : `Onboard a new ${childLabel.toLowerCase()}`}
          </DialogTitle>
          {parentTenant && (isGroup || isAgency) && !result && (
            <div className="text-xs text-zinc-500 mt-1" data-testid="tenant-parent-hint">
              Under <span className="font-semibold text-adb-navy">{parentTenant.name}</span>
              {isGroup && " group — cross-tenant reporting enabled"}
              {!isGroup && isAgency && " agency portfolio — billed at agency level"}
            </div>
          )}
        </DialogHeader>
        {!result ? (
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="text-xs">Company name <span className="text-red-500">*</span></Label>
              <Input data-testid="tenant-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm mt-1" placeholder="Acme Technologies" />
            </div>
            <div><Label className="text-xs">Slug <span className="text-red-500">*</span></Label>
              <Input data-testid="tenant-form-slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} className="rounded-sm mt-1 font-mono-plex" placeholder="acme" />
            </div>
            <div><Label className="text-xs">Pack</Label>
              <Select value={form.pack} onValueChange={(v) => setForm({ ...form, pack: v })}>
                <SelectTrigger data-testid="tenant-form-pack" className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{packs.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Pipeline stage</Label>
              <Select value={form.pipeline_stage} onValueChange={(v) => setForm({ ...form, pipeline_stage: v })}>
                <SelectTrigger data-testid="tenant-form-stage" className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{stages.map((s) => <SelectItem key={s} value={s}>{STAGE_META[s]?.label || s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">MRR (₹)</Label>
              <Input data-testid="tenant-form-mrr" type="number" value={form.mrr} onChange={(e) => setForm({ ...form, mrr: e.target.value })} className="rounded-sm mt-1 font-mono-plex" placeholder="0" />
            </div>
            <div><Label className="text-xs">Seat count</Label>
              <Input data-testid="tenant-form-seats" type="number" value={form.seat_count} onChange={(e) => setForm({ ...form, seat_count: e.target.value })} className="rounded-sm mt-1 font-mono-plex" placeholder="0" />
            </div>
            <div className="col-span-2"><Label className="text-xs">Trial expiry</Label>
              <Input type="date" value={form.trial_expiry} onChange={(e) => setForm({ ...form, trial_expiry: e.target.value })} className="rounded-sm mt-1 font-mono-plex" />
            </div>
            <div className="col-span-2"><Label className="text-xs">Sales notes</Label>
              <Textarea rows={2} value={form.sales_notes} onChange={(e) => setForm({ ...form, sales_notes: e.target.value })} className="rounded-sm mt-1" placeholder="Referred by …, key decision maker …" />
            </div>
            <div className="col-span-2 border-t border-zinc-100 pt-3 text-xs text-zinc-500 uppercase tracking-widest font-bold">First admin (optional)</div>
            <div><Label className="text-xs">Admin name</Label>
              <Input value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} className="rounded-sm mt-1" />
            </div>
            <div><Label className="text-xs">Admin email</Label>
              <Input data-testid="tenant-form-admin-email" type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value.toLowerCase() })} className="rounded-sm mt-1" placeholder="admin@acme.com" />
            </div>
          </div>
        ) : (
          <div data-testid="tenant-created-result" className="space-y-3">
            <div className="rounded-sm border border-adb-green/40 bg-adb-green/10 p-4">
              <div className="text-sm font-semibold text-adb-navy flex items-center gap-1">
                <CheckCircle size={14} weight="fill" className="text-adb-green" /> Tenant provisioned
              </div>
              <div className="text-xs text-zinc-600 mt-1">"{result.name}" · slug <code className="font-mono-plex">{result.slug}</code></div>
            </div>
            {result.admin_credentials && (
              <div className="rounded-sm border border-zenith-magenta/40 bg-zenith-magenta/10 p-4">
                <div className="text-sm font-semibold text-adb-navy">First admin login</div>
                <div className="text-xs text-zinc-600 mt-1">Share once — password is not stored in plain text after this dialog closes.</div>
                <div className="mt-3 space-y-2 text-sm font-mono-plex">
                  <div className="flex items-center gap-2">
                    <span className="text-zinc-500 w-16">Email:</span><span>{result.admin_credentials.email}</span>
                    <button onClick={() => copyToClipboard(result.admin_credentials.email)} className="ml-auto p-1 rounded-sm hover:bg-zinc-100"><Copy size={12} /></button>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-zinc-500 w-16">Password:</span><span className="font-bold">{result.admin_credentials.temporary_password}</span>
                    <button data-testid="tenant-copy-password" onClick={() => copyToClipboard(result.admin_credentials.temporary_password)} className="ml-auto p-1 rounded-sm hover:bg-zinc-100"><Copy size={12} /></button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" onClick={close} className="rounded-sm">Cancel</Button>
              <Button data-testid="tenant-form-submit" onClick={submit} disabled={busy} className="rounded-sm bg-adb-green text-adb-navy hover:bg-adb-green-600 font-semibold">
                {busy ? "Provisioning…" : "Create tenant"}
              </Button>
            </>
          ) : (
            <Button data-testid="tenant-created-close" onClick={close} className="rounded-sm bg-adb-navy hover:bg-adb-navy-800">Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---------------------------------------------------------------------------
// Tenants tab
// ---------------------------------------------------------------------------
function TenantsTab({ packs, stages, tenants, reload }) {
  const patch = async (id, patch) => {
    try { await api.patch(`/sales/tenants/${id}`, patch); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="overflow-x-auto border border-zinc-200 rounded-sm bg-white">
      <table className="hr-table">
        <thead>
          <tr><th>Tenant</th><th>Pack</th><th>Stage</th><th>MRR</th><th>Seats</th><th>Employees</th><th>Trial expiry</th><th>Notes</th></tr>
        </thead>
        <tbody>
          {tenants.length === 0 && <tr><td colSpan={8} className="text-center py-6 text-zinc-500 text-sm">No tenants yet.</td></tr>}
          {tenants.map((t) => (
            <tr key={t.id} data-testid={`sales-tenant-row-${t.id}`}>
              <td>
                <div className="text-sm font-medium text-adb-navy">{t.name}</div>
                <div className="text-[11px] font-mono-plex text-zinc-500">{t.slug}</div>
              </td>
              <td>
                <Select value={t.pack || "essentials"} onValueChange={(v) => patch(t.id, { pack: v })}>
                  <SelectTrigger data-testid={`sales-pack-${t.id}`} className={`h-7 w-32 rounded-sm text-xs border ${PACK_TONE[t.pack] || ""}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{packs.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </td>
              <td>
                <Select value={t.pipeline_stage || "active"} onValueChange={(v) => patch(t.id, { pipeline_stage: v })}>
                  <SelectTrigger data-testid={`sales-stage-${t.id}`} className={`h-7 w-28 rounded-sm text-xs border ${STAGE_META[t.pipeline_stage]?.tone || ""}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{stages.map((s) => <SelectItem key={s} value={s}>{STAGE_META[s]?.label || s}</SelectItem>)}</SelectContent>
                </Select>
              </td>
              <td>
                <Input data-testid={`sales-mrr-${t.id}`} defaultValue={t.mrr ?? 0} type="number" onBlur={(e) => patch(t.id, { mrr: parseFloat(e.target.value) || 0 })} className="h-7 w-24 rounded-sm text-xs font-mono-plex" />
              </td>
              <td className="font-mono-plex text-xs">{t.seat_count ?? 0}</td>
              <td className="font-mono-plex text-xs">{t.employees_count ?? 0}</td>
              <td className="font-mono-plex text-xs">{t.trial_expiry || "—"}</td>
              <td className="text-xs text-zinc-500 max-w-xs truncate" title={t.sales_notes}>{t.sales_notes || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Pipeline tab
// ---------------------------------------------------------------------------
function PipelineTab({ stages, reload }) {
  const [data, setData] = useState({ grouped: {}, counts: {}, total_mrr: 0, total_tenants: 0 });
  const load = async () => {
    try { const { data } = await api.get("/sales/pipeline"); setData(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [reload]);

  const move = async (tid, stage) => {
    try { await api.patch(`/sales/tenants/${tid}`, { pipeline_stage: stage }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <div className="flex items-center gap-4 mb-4 flex-wrap text-xs">
        <div className="text-zinc-500 uppercase tracking-widest font-bold">Pipeline value:</div>
        <div className="font-display text-lg font-bold text-adb-navy">{fmtINR(data.total_mrr)} MRR</div>
        <div className="text-zinc-500">{data.total_tenants} tenants</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        {stages.map((s) => {
          const items = data.grouped[s] || [];
          const stageMeta = STAGE_META[s];
          return (
            <div key={s} data-testid={`pipeline-col-${s}`} className="border border-zinc-200 rounded-sm bg-zinc-50 min-h-[240px]">
              <div className={`px-3 py-2 border-b ${stageMeta?.tone || ""} font-display font-bold text-sm flex items-center justify-between rounded-t-sm`}>
                <span>{stageMeta?.label || s}</span>
                <Badge variant="outline" className="rounded-sm text-[10px]">{items.length}</Badge>
              </div>
              <div className="p-2 space-y-2">
                {items.map((t) => (
                  <div key={t.id} data-testid={`pipeline-card-${t.id}`} className="border border-zinc-200 rounded-sm bg-white p-2 hover:border-adb-navy/30 transition-colors">
                    <div className="text-sm font-medium text-adb-navy truncate">{t.name}</div>
                    <div className="flex items-center gap-1 mt-1 flex-wrap">
                      <Badge variant="outline" className={`rounded-sm text-[10px] ${PACK_TONE[t.pack] || ""}`}>{t.pack || "essentials"}</Badge>
                      {t.mrr > 0 && <span className="text-[11px] font-mono-plex text-zinc-500">{fmtINR(t.mrr)}</span>}
                    </div>
                    <Select value="" onValueChange={(v) => move(t.id, v)}>
                      <SelectTrigger className="h-6 w-full rounded-sm text-[10px] mt-2 border-zinc-200"><SelectValue placeholder="Move to…" /></SelectTrigger>
                      <SelectContent>
                        {stages.filter((x) => x !== s).map((x) => <SelectItem key={x} value={x}>{STAGE_META[x]?.label || x}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
                {items.length === 0 && <div className="text-[11px] text-zinc-400 text-center py-4">Empty</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Usage tab
// ---------------------------------------------------------------------------
function UsageTab({ tenants }) {
  const [tid, setTid] = useState(tenants[0]?.id || "");
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    if (!tid) return;
    api.get(`/sales/usage/${tid}`).then(({ data }) => setUsage(data)).catch(() => setUsage(null));
  }, [tid]);

  useEffect(() => { if (!tid && tenants[0]) setTid(tenants[0].id); }, [tenants, tid]);

  const m = usage?.metrics || {};
  const cards = [
    { label: "Employees", v: m.employees, icon: Users, tone: "teal" },
    { label: "Active users", v: m.active_users, icon: Users, tone: "green" },
    { label: "Logins (7 d)", v: m.logins_last_7d, icon: ClockClockwise, tone: "blue" },
    { label: "Payslips generated", v: m.payslips_generated, icon: ChartLineUp, tone: "magenta" },
    { label: "Leaves (30 d)", v: m.leaves_last_30d, icon: ClockClockwise, tone: "teal" },
    { label: "Reimbursement claims", v: m.reimbursement_claims, icon: CurrencyInr, tone: "green" },
    { label: "Projects", v: m.projects, icon: Package, tone: "blue" },
    { label: "Open tasks", v: m.open_tasks, icon: ClockClockwise, tone: "magenta" },
  ];

  const TONE = {
    teal: { bg: "bg-zenith-teal/10", border: "border-zenith-teal/30", icon: "text-zenith-teal-700" },
    magenta: { bg: "bg-zenith-magenta/10", border: "border-zenith-magenta/30", icon: "text-zenith-magenta" },
    green: { bg: "bg-zenith-green/10", border: "border-zenith-green/30", icon: "text-zenith-green" },
    blue: { bg: "bg-zenith-blue-500/10", border: "border-zenith-blue-500/30", icon: "text-zenith-blue-700" },
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Label className="text-xs uppercase tracking-widest font-bold text-zinc-500">Tenant:</Label>
        <Select value={tid} onValueChange={setTid}>
          <SelectTrigger data-testid="usage-tenant-select" className="rounded-sm w-64 h-8 text-sm"><SelectValue placeholder="Pick a tenant" /></SelectTrigger>
          <SelectContent>{tenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {usage ? (
        <>
          <div className="flex items-center gap-3 flex-wrap">
            <Badge variant="outline" className={`rounded-sm ${PACK_TONE[usage.pack] || ""}`}>Pack: {usage.pack}</Badge>
            <Badge variant="outline" className={`rounded-sm ${STAGE_META[usage.pipeline_stage]?.tone || ""}`}>Stage: {STAGE_META[usage.pipeline_stage]?.label}</Badge>
            {usage.mrr > 0 && <span className="text-xs font-mono-plex text-zinc-600">MRR: {fmtINR(usage.mrr)}</span>}
            {usage.trial_expiry && <span className="text-xs font-mono-plex text-zinc-600">Trial ends: {usage.trial_expiry}</span>}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {cards.map((c) => {
              const t = TONE[c.tone];
              const Icon = c.icon;
              return (
                <div key={c.label} className={`border ${t.border} ${t.bg} rounded-sm px-4 py-3 flex items-center gap-3`}>
                  <div className={`w-9 h-9 grid place-items-center rounded-sm bg-white ${t.border} border`}>
                    <Icon size={18} weight="duotone" className={t.icon} />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">{c.label}</div>
                    <div className="font-display text-2xl font-bold text-adb-navy leading-none">{c.v ?? "—"}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div className="text-xs text-zinc-500">Pick a tenant to see metrics.</div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Packs tab (read-only marketing surface)
// ---------------------------------------------------------------------------
function PacksTab({ packs }) {
  const fmt = (n) => n == null ? "Contact us" : `₹${n}`;
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
      {packs.map((p) => (
        <div key={p.code} data-testid={`pack-card-${p.code}`} className={`border rounded-sm p-4 bg-white ${p.highlight ? "border-zenith-magenta shadow-lg" : "border-zinc-200"}`}>
          {p.highlight && <Badge variant="outline" className="rounded-sm bg-zenith-magenta/10 border-zenith-magenta/30 text-zenith-magenta mb-2">Most popular</Badge>}
          <div className="font-display text-2xl font-bold text-adb-navy">{p.name}</div>
          <div className="text-[11px] uppercase tracking-widest font-bold text-zenith-teal mt-1">{p.target}</div>
          <div className="mt-3">
            <span className="font-display text-3xl font-bold text-adb-navy">{fmt(p.price_inr)}</span>
            {p.price_inr != null && <span className="text-xs text-zinc-500"> /seat / month</span>}
            {p.annual_discount_percent > 0 && (
              <div className="text-[10px] text-zenith-green font-semibold mt-0.5">Save {p.annual_discount_percent}% billed annually</div>
            )}
          </div>
          <p className="text-xs text-zinc-500 mt-3">{p.tagline}</p>
          <ul className="text-xs mt-3 space-y-1">
            {p.modules.map((m) => (
              <li key={m} className="flex items-start gap-1"><CheckCircle size={12} weight="fill" className="text-zenith-green mt-0.5 shrink-0" /> {m}</li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function SalesConsole() {
  const { user } = useAuth();
  const allowed = user?.role === "super_admin";

  const [packs, setPacks] = useState([]);
  const [stages, setStages] = useState(["lead", "demo", "trial", "active", "churned"]);
  const [tenants, setTenants] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = async () => {
    try {
      const [pRes, tRes] = await Promise.all([api.get("/sales/packs"), api.get("/sales/tenants")]);
      setPacks(pRes.data.packs);
      setStages(pRes.data.pipeline_stages);
      setTenants(tRes.data);
      setRefreshKey((k) => k + 1);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { if (allowed) load(); /* eslint-disable-next-line */ }, [allowed]);

  const summary = useMemo(() => {
    const active = tenants.filter((t) => t.pipeline_stage === "active").length;
    const trial = tenants.filter((t) => t.pipeline_stage === "trial").length;
    const mrr = tenants.reduce((s, t) => s + (parseFloat(t.mrr) || 0), 0);
    const seats = tenants.reduce((s, t) => s + (parseInt(t.seat_count) || 0), 0);
    return { active, trial, mrr, seats };
  }, [tenants]);

  if (!allowed) {
    return (
      <div className="border border-red-200 bg-red-50 text-red-700 rounded-sm p-6 text-sm">
        <LockKey size={20} weight="duotone" className="mb-2" /> This workspace is restricted to the agency super_admin.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-zenith-magenta font-bold">Sales Console</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Client accounts &amp; pipeline.</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">Onboard tenants, move deals through the pipeline, monitor usage, and manage the pack each customer is on.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} className="rounded-sm"><ArrowsClockwise size={14} className="mr-1" /> Refresh</Button>
          <NewTenantDialog packs={packs} stages={stages} onCreated={load} />
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard label="Total tenants" value={tenants.length} icon={Buildings} tone="teal" />
        <SummaryCard label="Active" value={summary.active} icon={CheckCircle} tone="green" />
        <SummaryCard label="On trial" value={summary.trial} icon={ClockClockwise} tone="magenta" />
        <SummaryCard label="Portfolio MRR" value={fmtINR(summary.mrr)} icon={CurrencyInr} tone="blue" />
      </div>

      <Tabs defaultValue="tenants">
        <TabsList className="bg-zinc-100 border border-zinc-200 rounded-sm">
          <TabsTrigger value="tenants" className="rounded-sm" data-testid="tab-tenants"><Buildings size={12} className="mr-1" /> Tenants</TabsTrigger>
          <TabsTrigger value="pipeline" className="rounded-sm" data-testid="tab-pipeline"><Kanban size={12} className="mr-1" /> Pipeline</TabsTrigger>
          <TabsTrigger value="usage" className="rounded-sm" data-testid="tab-usage"><ChartLineUp size={12} className="mr-1" /> Usage</TabsTrigger>
          <TabsTrigger value="packs" className="rounded-sm" data-testid="tab-packs"><Package size={12} className="mr-1" /> Packs</TabsTrigger>
        </TabsList>
        <TabsContent value="tenants" className="mt-4"><TenantsTab packs={packs} stages={stages} tenants={tenants} reload={load} /></TabsContent>
        <TabsContent value="pipeline" className="mt-4"><PipelineTab stages={stages} reload={refreshKey} /></TabsContent>
        <TabsContent value="usage" className="mt-4"><UsageTab tenants={tenants} /></TabsContent>
        <TabsContent value="packs" className="mt-4"><PacksTab packs={packs} /></TabsContent>
      </Tabs>
    </div>
  );
}


const TONE_MAP = {
  teal: { bg: "bg-zenith-teal/10", border: "border-zenith-teal/30", icon: "text-zenith-teal-700" },
  magenta: { bg: "bg-zenith-magenta/10", border: "border-zenith-magenta/30", icon: "text-zenith-magenta" },
  green: { bg: "bg-zenith-green/10", border: "border-zenith-green/30", icon: "text-zenith-green" },
  blue: { bg: "bg-zenith-blue-500/10", border: "border-zenith-blue-500/30", icon: "text-zenith-blue-700" },
};
function SummaryCard({ label, value, icon: Icon, tone }) {
  const t = TONE_MAP[tone] || TONE_MAP.teal;
  return (
    <div className={`border ${t.border} ${t.bg} rounded-sm px-4 py-3 flex items-center gap-3`}>
      <div className={`w-9 h-9 grid place-items-center rounded-sm bg-white ${t.border} border`}>
        <Icon size={18} weight="duotone" className={t.icon} />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">{label}</div>
        <div className="font-display text-2xl font-bold text-adb-navy leading-none">{value}</div>
      </div>
    </div>
  );
}
